import React, { useEffect, useRef, useState } from 'react';
import * as _ from 'lodash';
import { useHistory, generatePath } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  Tooltip,
} from '@mui/material';
import { indigo } from '@mui/material/colors';
import { ArrowDownward, ArrowUpward, Help as HelpIcon } from '@mui/icons-material';
import TableToCSVGenerator from './TableToCSVGenerator';
import { formatDate, formatNumber, formatValueOfMeals, csvFileNameGenerators } from '../helpers/formatters';
import { getUserFullName } from '../helpers/user';
import routes from '../routes';
import { Colors } from '../assets/theme/Colors';

const paperSx = {
  padding: { sx: 1, sm: 2 },
  '& table': {
    '& thead': {
      '& th': {
        fontWeight: 600,
        padding: '3px 12px',
        whiteSpace: 'nowrap',
      },
    },
    '& tbody': {
      '& td': {
        padding: '3px 12px',
        borderBottom: 'none',
      },
      '& td:first-child': {
        minWidth: '200px',
      },
    },
  },
};
const rowTotalSx = {
  backgroundColor: 'primary.main',
  color: '#fff',
  fontWeight: 600,
};
const footerTotalColumnSx = {
  left: 0,
  position: 'sticky',
};
const oddColumnSx = {
  background: `${indigo[50]} !important`,
};
const diffOddSx = {
  color: `${Colors.tableDiff.odd.contrastText} !important`,
  boxShadow: `inset 0px 0px 1px 100px ${Colors.tableDiff.odd.color}`,
};
const diffEvenSx = {
  color: `${Colors.tableDiff.even.contrastText} !important`,
  boxShadow: `inset 0px 0px 1px 100px ${Colors.tableDiff.even.color}`,
};
const totalDeltaColumnSx = {
  borderLeft: `2px solid ${indigo[300]}`,
};
const totalDeltaHeaderSx = {
  minWidth: '215px',
  textAlign: 'center',
};
const periodStickyHeaderSx = {
  top: 0,
  position: 'sticky',
  zIndex: 1,
  background: '#fff',
};
const entityTypeStickyHeaderSx = {
  top: 36,
  position: 'sticky',
  zIndex: 1,
  background: '#fff',
};
const measureStickyHeaderSx = {
  top: 72,
  position: 'sticky',
  zIndex: 1,
  background: '#fff',
  boxShadow: 'inset 0 -2px 0px 0px #7986cb',
  '& span': {
    userSelect: 'none',
  },
};
const siteColumnHeadSx = {
  position: 'sticky',
  top: 0,
  left: 0,
  background: '#fff',
  zIndex: 2,
  boxShadow: 'inset 0 -2px 0px 0px #7986cb',
};
const siteColumnSx = {
  maxWidth: '200px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  position: 'sticky',
  left: 0,
  background: '#fff',
};
const siteDirectorColumnHeadSx = {
  position: 'sticky',
  top: 0,
  left: '200px',
  background: '#fff',
  boxShadow: '2px 0 0px 0px #7986cb, inset 0 -2px 0px 0px #7986cb',
  zIndex: 2,
};
const siteDirectorColumnSx = {
  maxWidth: '200px',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  position: 'sticky',
  left: 200,
  background: '#fff',
  boxShadow: '2px 0 0px 0px #7986cb',
};
const sortByCellSx = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  cursor: 'pointer',
};
const justifyContentCenterSx = {
  justifyContent: 'center',
};
const descriptionsHeaderSx = {
  borderBottom: 'none',
};
const tableContainerSx = {
  height: 'calc(100vh - 400px)',
  overflow: 'scroll',
  '&::-webkit-scrollbar': {
    WebkitAppearance: 'none',
    width: '0.65rem',
    height: '0.65rem',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: 'rgba(0, 0, 0, .5)',
    WebkitBoxShadow: '0 0 1px rgba(255, 255, 255, .5)',
  },
  '@media (max-height: 680px)': {
    height: '50vh',
  },
};

const GrowthReportTable = ({ granularity, growth, filters }) => {
  const history = useHistory();
  const tableRef = useRef();
  const [growthReport, setGrowthReport] = useState(growth);
  const [orderBy, setOrderBy] = useState({
    period: null,
    entityType: 'site',
    measure: null,
    order: 'asc',
  });

  useEffect(() => {
    setGrowthReport(growth);
  }, [growth]);

  const reverseNegative = (number) => (number === 0 ? number : -number);

  if (!growthReport.sites) {
    return (
      <Box my={2}>
        <Paper sx={paperSx}>Please use &quot;RUN&quot; button to generate the report.</Paper>
      </Box>
    );
  }

  const handleSortBy = (period, entityType, measure) => {
    const foundOrderBy =
      orderBy.period === period && orderBy.entityType === entityType && orderBy.measure === measure
        ? orderBy.order
        : null;
    const order = foundOrderBy ? (foundOrderBy === 'asc' ? 'desc' : 'asc') : 'asc';

    setGrowthReport({
      ...growthReport,
      sites: _.orderBy(
        growthReport.sites,
        row => {
          if (measure !== null) {
            if (period !== null) {
              const value = row[entityType].periods[period][measure];

              return value !== null ? value : -1;
            }

            // period is not set, we want to sort by row total
            const value = row[entityType].total[measure];

            return value !== null ? value : -1;
          }

          if (entityType === 'site') {
            return row.site.name;
          }

          return `${row.site.siteDirector.firstname} ${row.site.siteDirector.lastname}`;
        },
        order
      ),
    });

    setOrderBy({
      period: period,
      entityType: entityType,
      measure: measure,
      order: order,
    });
  };

  const renderCount = (count, format = false) => {
    if (format === 'meals_value') {
      return formatValueOfMeals(count);
    }

    return formatNumber(count);
  };

  const renderDelta = (delta, format = false) => {
    if (format === 'meals_value') {
      return formatValueOfMeals(delta);
    }

    return formatNumber(delta);
  };

  const renderDeltaPercentage = deltaPercentage =>
    deltaPercentage !== null ? `${Number(deltaPercentage.toPrecision(2))}%` : 'n/a';

  const renderEntityColumn = (index, entityName, showBorder = false, isOdd = null) => (
    <TableCell
      colSpan={index > 0 ? 3 : 1}
      variant="head"
      align="center"
      sx={[
        entityTypeStickyHeaderSx,
        descriptionsHeaderSx,
        showBorder && totalDeltaColumnSx,
        isOdd === true && diffOddSx,
        isOdd === false && diffEvenSx,
      ]}
    >
      {entityName}
    </TableCell>
  );

  const renderEntityColumnTotal = (entityName, showBorder = false, isOdd = null) => (
    <TableCell
      colSpan={2}
      variant="head"
      align="center"
      sx={[
        entityTypeStickyHeaderSx,
        descriptionsHeaderSx,
        showBorder && totalDeltaColumnSx,
        isOdd === true && diffOddSx,
        isOdd === false && diffEvenSx,
      ]}
    >
      {entityName}
    </TableCell>
  );

  const renderSortBy = (index, entityType, measure, label) => (
    <Box
      onClick={() => handleSortBy(index, entityType, measure)}
      sx={[sortByCellSx, (entityType !== 'site' && entityType !== 'siteDirector') && justifyContentCenterSx]}
    >
      <>
        <span>
          {measure === 'count' && '#'}

          {measure === 'delta' && 'Δ'}

          {measure === 'delta_percentage' && 'Δ%'}

          {label !== null && label}
        </span>

        {orderBy.period === index && orderBy.entityType === entityType && orderBy.measure === measure && (
          <>{orderBy.order === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}</>
        )}

        {measure === 'delta' && (
          <Tooltip title={`Increase or decrease since last ${granularity === 'monthly' ? 'month' : 'week'}.`}>
            <HelpIcon fontSize="small" htmlColor={Colors.blue.main} />
          </Tooltip>
        )}

        {measure === 'delta_percentage' && (
          <Tooltip title={`Percentage increase or decrease since last ${granularity === 'monthly' ? 'month' : 'week'}.`}>
            <HelpIcon fontSize="small" htmlColor={Colors.blue.main} />
          </Tooltip>
        )}
      </>
    </Box>
  );

  const renderEntitySubColumn = (index, entityType, showBorder = false, isOdd = null) => (
    <>
      <TableCell
        variant="head"
        align="center"
        sx={[
          measureStickyHeaderSx,
          descriptionsHeaderSx,
          showBorder && totalDeltaColumnSx,
          isOdd === true && diffOddSx,
          isOdd === false && diffEvenSx,
        ]}
      >
        {renderSortBy(index, entityType, 'count')}
      </TableCell>

      {index > 0 && (
        <>
          <TableCell
            variant="head"
            align="center"
            sx={[
              measureStickyHeaderSx,
              descriptionsHeaderSx,
              isOdd === true && diffOddSx,
              isOdd === false && diffEvenSx,
            ]}
          >
            {renderSortBy(index, entityType, 'delta')}
          </TableCell>

          <TableCell
            variant="head"
            align="center"
            sx={[
              measureStickyHeaderSx,
              descriptionsHeaderSx,
              isOdd === true && diffOddSx,
              isOdd === false && diffEvenSx,
            ]}
          >
            {renderSortBy(index, entityType, 'delta_percentage')}
          </TableCell>
        </>
      )}
    </>
  );

  const renderEntitySubColumnTotal = (entityType, showBorder = false, isOdd = null) => (
    <>
      <TableCell
        variant="head"
        align="center"
        sx={[
          measureStickyHeaderSx,
          descriptionsHeaderSx,
          showBorder && totalDeltaColumnSx,
          isOdd === true && diffOddSx,
          isOdd === false && diffEvenSx,
        ]}
      >
        {renderSortBy(null, entityType, 'delta')}
      </TableCell>

      <TableCell
        variant="head"
        align="center"
        sx={[
          measureStickyHeaderSx,
          descriptionsHeaderSx,
          isOdd === true && diffOddSx,
          isOdd === false && diffEvenSx,
        ]}
      >
        {renderSortBy(null, entityType, 'delta_percentage')}
      </TableCell>
    </>
  );

  const renderRow = (index, siteGrowthReport) => {
    const _renderRowEntity = (
      index,
      index2,
      siteGrowthReportEntity,
      showBorder = false,
      format = false,
      isOdd = null,
      reverseNegativeValue = false
    ) => (
      <>
        <TableCell
          variant="head"
          align="right"
          sx={[index % 2 && oddColumnSx, isOdd === true && diffOddSx, isOdd === false && diffEvenSx, showBorder && totalDeltaColumnSx ]}
          data-period={index2}
        >
          {renderCount(
            reverseNegativeValue ? reverseNegative(siteGrowthReportEntity.count) : siteGrowthReportEntity.count,
            format
          )}
        </TableCell>

        {index2 > 0 && (
          <>
            <TableCell
              variant="head"
              align="right"
              sx={[index % 2 && oddColumnSx, isOdd === true && diffOddSx, isOdd === false && diffEvenSx ]}
            >
              {renderDelta(
                reverseNegativeValue ? reverseNegative(siteGrowthReportEntity.delta) : siteGrowthReportEntity.delta,
                format
              )}
            </TableCell>

            <TableCell
              variant="head"
              align="right"
              sx={[index % 2 && oddColumnSx, isOdd === true && diffOddSx, isOdd === false && diffEvenSx ]}
            >
              {renderDeltaPercentage(siteGrowthReportEntity.delta_percentage)}
            </TableCell>
          </>
        )}
      </>
    );

    const _renderRowEntityTotal = (
      index,
      siteGrowthReportEntityTotal,
      showBorder = false,
      format = false,
      isOdd = null,
      reverseNegativeValue = false
    ) => (
      <>
        <TableCell
          variant="head"
          align="right"
          sx={[index % 2 && oddColumnSx, isOdd === true && diffOddSx, isOdd === false && diffEvenSx, showBorder && totalDeltaColumnSx ]}
        >
          {renderDelta(reverseNegativeValue ? reverseNegative(siteGrowthReportEntityTotal.delta) : siteGrowthReportEntityTotal.delta, format)}
        </TableCell>

        <TableCell
          variant="head"
          align="right"
          sx={[index % 2 && oddColumnSx, isOdd === true && diffOddSx, isOdd === false && diffEvenSx ]}
        >
          {renderDeltaPercentage(siteGrowthReportEntityTotal.delta_percentage)}
        </TableCell>
      </>
    );

    return (
      <TableRow>
        <TableCell sx={[index % 2 && oddColumnSx, siteColumnSx]}>{siteGrowthReport.site.name}</TableCell>

        <TableCell sx={[index % 2 && oddColumnSx, siteDirectorColumnSx]}>
          {siteGrowthReport.site.siteDirector && (
            <Link
              onClick={() =>
                history.push(generatePath(routes.userEditNew, { id: siteGrowthReport.site.siteDirector.id }))
              }
              style={{ cursor: 'pointer' }}
              underline="hover"
            >
              {getUserFullName(siteGrowthReport.site.siteDirector)}
            </Link>
          )}

          {!siteGrowthReport.site.siteDirector && 'n/a'}
        </TableCell>

        {growthReport.periods.map((row, index2) => (
          <React.Fragment key={index2}>
            {_renderRowEntity(index, index2, siteGrowthReport.foodDonors.periods[index2], true)}

            {_renderRowEntity(index, index2, siteGrowthReport.receivers.periods[index2])}

            {_renderRowEntity(index, index2, siteGrowthReport.rescuers.periods[index2])}

            {_renderRowEntity(index, index2, siteGrowthReport.rescues.periods[index2])}

            {_renderRowEntity(index, index2, siteGrowthReport.meals.periods[index2])}

            {_renderRowEntity(index, index2, siteGrowthReport.mealsLbs.periods[index2])}

            {_renderRowEntity(index, index2, siteGrowthReport.lbsValue.periods[index2], false, 'meals_value')}

            {_renderRowEntity(index, index2, siteGrowthReport.co2eq.periods[index2], false, false, null, true)}

            {_renderRowEntity(index, index2, siteGrowthReport.water.periods[index2], false, false, null, true)}
          </React.Fragment>
        ))}

        <React.Fragment>
          {_renderRowEntityTotal(index, siteGrowthReport.foodDonors.total, true)}

          {_renderRowEntityTotal(index, siteGrowthReport.receivers.total)}

          {_renderRowEntityTotal(index, siteGrowthReport.rescuers.total)}

          {_renderRowEntityTotal(index, siteGrowthReport.rescues.total)}

          {_renderRowEntityTotal(index, siteGrowthReport.meals.total)}

          {_renderRowEntityTotal(index, siteGrowthReport.mealsLbs.total)}

          {_renderRowEntityTotal(index, siteGrowthReport.lbsValue.total, false, 'meals_value')}

          {_renderRowEntityTotal(index, siteGrowthReport.co2eq.total, false, false, null, true)}

          {_renderRowEntityTotal(index, siteGrowthReport.water.total, false, false, null, true)}
        </React.Fragment>
      </TableRow>
    );
  };

  const renderTotalRow = growthTotal => {
    const _renderTotalRowEntityPeriod = (index, siteGrowthReportEntityTotalPeriod, format = false, reverseNegativeValue = false)  => (
      <>
        <TableCell
          variant="head"
          align="right"
          sx={rowTotalSx}
        >
          {renderCount(
            reverseNegativeValue
              ? reverseNegative(siteGrowthReportEntityTotalPeriod.count)
              : siteGrowthReportEntityTotalPeriod.count,
            format
          )}
        </TableCell>

        {index > 0 && (
          <>
            <TableCell
              variant="head"
              align="right"
              sx={rowTotalSx}
            >
              {renderDelta(
                reverseNegativeValue
                  ? reverseNegative(siteGrowthReportEntityTotalPeriod.delta)
                  : siteGrowthReportEntityTotalPeriod.delta,
                format
              )}
            </TableCell>

            <TableCell
              variant="head"
              align="right"
              sx={rowTotalSx}
            >
              {renderDeltaPercentage(siteGrowthReportEntityTotalPeriod.delta_percentage)}
            </TableCell>
          </>
        )}
      </>
    );

    const _renderTotalRowEntityTotal = (siteGrowthReportEntityTotal, format = false, reverseNegativeValue = false) => (
      <>
        <TableCell
          variant="head"
          align="right"
          sx={rowTotalSx}
        >
          {renderDelta(
            reverseNegativeValue ? reverseNegative(siteGrowthReportEntityTotal.delta) : siteGrowthReportEntityTotal.delta,
            format
          )}
        </TableCell>

        <TableCell
          variant="head"
          align="right"
          sx={rowTotalSx}
        >
          {renderDeltaPercentage(siteGrowthReportEntityTotal.delta_percentage)}
        </TableCell>
      </>
    );

    return (
      <TableRow>
        <TableCell
          colSpan={2}
          variant="head"
          sx={{...rowTotalSx, ...footerTotalColumnSx}}
        >
          Total
        </TableCell>

        {growthReport.periods.map((row, index) => (
          <React.Fragment key={index}>
            {_renderTotalRowEntityPeriod(index, growthTotal.foodDonors.periods[index])}

            {_renderTotalRowEntityPeriod(index, growthTotal.receivers.periods[index])}

            {_renderTotalRowEntityPeriod(index, growthTotal.rescuers.periods[index])}

            {_renderTotalRowEntityPeriod(index, growthTotal.rescues.periods[index])}

            {_renderTotalRowEntityPeriod(index, growthTotal.meals.periods[index])}

            {_renderTotalRowEntityPeriod(index, growthTotal.mealsLbs.periods[index])}

            {_renderTotalRowEntityPeriod(index, growthTotal.lbsValue.periods[index], 'meals_value')}

            {_renderTotalRowEntityPeriod(index, growthTotal.co2eq.periods[index], false, true)}

            {_renderTotalRowEntityPeriod(index, growthTotal.water.periods[index], false, true)}
          </React.Fragment>
        ))}

        {_renderTotalRowEntityTotal(growthTotal.foodDonors.total)}

        {_renderTotalRowEntityTotal(growthTotal.receivers.total)}

        {_renderTotalRowEntityTotal(growthTotal.rescuers.total)}

        <React.Fragment>
          {_renderTotalRowEntityTotal(growthTotal.rescues.total)}

          {_renderTotalRowEntityTotal(growthTotal.meals.total)}

          {_renderTotalRowEntityTotal(growthTotal.mealsLbs.total)}

          {_renderTotalRowEntityTotal(growthTotal.lbsValue.total, 'meals_value')}

          {_renderTotalRowEntityTotal(growthTotal.co2eq.total, false, true)}
          
          {_renderTotalRowEntityTotal(growthTotal.water.total, false, true)}
        </React.Fragment>
      </TableRow>
    );
  };

  // Generate dynamic filename based on date range
  const generateCsvFileName = () => {
    return csvFileNameGenerators.growthReport({
      dateRange: filters?.dateRange
    });
  };

  return (
    <Box my={2}>
      <Paper sx={paperSx}>
        <TableToCSVGenerator tableRef={tableRef} fileName={generateCsvFileName()} />

        <TableContainer sx={tableContainerSx} className="table-container">
          <Table
            aria-label="spanning table"
            sx={{ borderCollapse: 'separate' }}
            size="small"
            ref={tableRef}
            id="growth-report-table"
          >
            <TableHead>
              <TableRow>
                <TableCell
                  rowSpan={3}
                  sx={[descriptionsHeaderSx, siteColumnHeadSx]}
                >
                  {renderSortBy(null, 'site', null, 'Site')}
                </TableCell>

                <TableCell
                  rowSpan={3}
                  sx={[descriptionsHeaderSx, siteDirectorColumnHeadSx]}
                >
                  {renderSortBy(null, 'siteDirector', null, 'Site Director')}
                </TableCell>

                {growthReport.periods.map((row, index) => {
                  const colSpanLength = (index > 0 ? 9 * 3 : 9);

                  return (
                    <React.Fragment key={index}>
                      <TableCell
                        colSpan={colSpanLength}
                        variant="head"
                        align="center"
                        sx={[periodStickyHeaderSx, descriptionsHeaderSx, index > 0 && totalDeltaColumnSx]}
                      >
                        {index === 0 && 'As of '}
                        {formatDate(row)}
                      </TableCell>
                    </React.Fragment>
                  );
                })}

                <TableCell
                  colSpan={18}
                  sx={[periodStickyHeaderSx, descriptionsHeaderSx, totalDeltaColumnSx, totalDeltaHeaderSx]}
                >
                  {`${formatDate(growthReport.periods[0])}` +
                  ` - ${formatDate(growthReport.periods[growthReport.periods.length - 1])}`}
                </TableCell>
              </TableRow>

              <TableRow>
                {growthReport.periods.map((row, index) => (
                  <React.Fragment key={index}>
                    {renderEntityColumn(index, 'Food Donors', true)}

                    {renderEntityColumn(index, 'Receivers')}

                    {renderEntityColumn(index, 'Rescuers')}

                    {renderEntityColumn(index, 'Rescues')}

                    {renderEntityColumn(index, 'Meals')}

                    {renderEntityColumn(index, 'Lbs')}

                    {renderEntityColumn(index, '$')}

                    {renderEntityColumn(index, 'Co2eq offset')}

                    {renderEntityColumn(index, 'Water offset')}
                  </React.Fragment>
                ))}

                {renderEntityColumnTotal('Food Donors', true)}

                {renderEntityColumnTotal('Receivers')}

                {renderEntityColumnTotal('Rescuers')}

                <React.Fragment>
                  {renderEntityColumnTotal('Rescues')}

                  {renderEntityColumnTotal('Meals')}

                  {renderEntityColumnTotal('Lbs')}

                  {renderEntityColumnTotal('$')}

                  {renderEntityColumnTotal('Co2eq offset')}

                  {renderEntityColumnTotal('Water offset')}
                </React.Fragment>
              </TableRow>

              <TableRow>
                {growthReport.periods.map((row, index) => (
                  <React.Fragment key={index}>
                    {renderEntitySubColumn(index, 'foodDonors', true)}

                    {renderEntitySubColumn(index, 'receivers')}

                    {renderEntitySubColumn(index, 'rescuers')}

                    {renderEntitySubColumn(index, 'rescues')}

                    {renderEntitySubColumn(index, 'meals')}

                    {renderEntitySubColumn(index, 'mealsLbs')}

                    {renderEntitySubColumn(index, 'lbsValue')}

                    {renderEntitySubColumn(index, 'co2eq')}

                    {renderEntitySubColumn(index, 'water')}
                  </React.Fragment>
                ))}

                {renderEntitySubColumnTotal('foodDonors', true)}

                {renderEntitySubColumnTotal('receivers')}

                {renderEntitySubColumnTotal('rescuers')}

                <React.Fragment>
                  {renderEntitySubColumnTotal('rescues')}

                  {renderEntitySubColumnTotal('meals')}

                  {renderEntitySubColumnTotal('mealsLbs')}

                  {renderEntitySubColumnTotal('lbsValue')}

                  {renderEntitySubColumnTotal('co2eq')}

                  {renderEntitySubColumnTotal('water')}
                </React.Fragment>
              </TableRow>
            </TableHead>

            <TableBody>
              {growthReport.sites.map((row, index) => (
                <React.Fragment key={index}>{renderRow(index, row)}</React.Fragment>
              ))}

              {renderTotalRow(growthReport.total)}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default GrowthReportTable;
